/**
 * Ticket lifecycle state machine.
 * Single source of truth for ticket status transitions.
 * Feature code MUST call `canTransition()` / `applyTransition()`;
 * inline `if (status === '...')` checks are forbidden — see do-not-do.md.
 */

import type { TicketStatus } from '../schemas/ticket.js';
import { InvalidTransitionError } from '../errors.js';

export type ActorRole = 'owner' | 'manager' | 'cashier' | 'washer' | 'customer' | 'system';

export interface Actor {
  uid: string;
  role: ActorRole;
}

export interface TenantQueueConfig {
  /** When true, washer is permitted to reorder + advance non-bay-assigned tickets. */
  readonly washerCanReorder: boolean;
}

type AllowedRoles = readonly ActorRole[];

/**
 * Transition table: { from: { to: allowedRoles } }.
 * Roles listed may perform the transition. 'system' indicates only Cloud Function may.
 */
const TRANSITIONS = {
  // Appointments start here. They carry no queue position and are excluded from
  // live-queue ordering / wait math until an operator ADMITS them into the
  // line (`scheduled → inQueue`). A no-show / unwanted booking is CANCELLED.
  scheduled: {
    inQueue: ['owner', 'manager', 'cashier'] as AllowedRoles,
    cancelled: ['owner', 'manager', 'cashier', 'customer'] as AllowedRoles,
  },
  inQueue: {
    inWashingProcess: ['owner', 'manager', 'cashier', 'washer'] as AllowedRoles,
    cancelled: ['owner', 'manager', 'customer'] as AllowedRoles,
    hold: ['owner', 'manager', 'cashier'] as AllowedRoles,
  },
  inWashingProcess: {
    readyForCollection: ['owner', 'manager', 'washer'] as AllowedRoles,
    cancelled: ['owner', 'manager'] as AllowedRoles,
  },
  readyForCollection: {
    collected: ['owner', 'manager', 'cashier', 'washer'] as AllowedRoles,
  },
  hold: {
    inQueue: ['owner', 'manager', 'cashier'] as AllowedRoles,
    cancelled: ['owner', 'manager'] as AllowedRoles,
  },
  collected: {},
  cancelled: {},
} as const satisfies Record<TicketStatus, Partial<Record<TicketStatus, AllowedRoles>>>;

export const TERMINAL_STATUSES: ReadonlySet<TicketStatus> = new Set(['collected', 'cancelled']);

export interface TransitionContext {
  /** Customers may only cancel their own ticket. */
  readonly customerOwnsTicket?: boolean;
}

export function canTransition(
  from: TicketStatus,
  to: TicketStatus,
  actor: Actor,
  ctx: TransitionContext = {},
): boolean {
  const fromRow = TRANSITIONS[from];
  const allowedRoles = (fromRow as Partial<Record<TicketStatus, AllowedRoles>>)[to];
  if (!allowedRoles) return false;
  if (!allowedRoles.includes(actor.role)) return false;
  if (actor.role === 'customer' && !ctx.customerOwnsTicket) return false;
  return true;
}

export function assertCanTransition(
  from: TicketStatus,
  to: TicketStatus,
  actor: Actor,
  ctx: TransitionContext = {},
): void {
  if (!canTransition(from, to, actor, ctx)) {
    throw new InvalidTransitionError(from, to, { actor, ctx });
  }
}

export function isTerminal(status: TicketStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function allowedNextStatuses(
  from: TicketStatus,
  actor: Actor,
  ctx: TransitionContext = {},
): readonly TicketStatus[] {
  const fromRow = TRANSITIONS[from];
  return (Object.keys(fromRow) as TicketStatus[]).filter((to) =>
    canTransition(from, to, actor, ctx),
  );
}
